import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { Check, X, Loader2, ArrowRight, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils.js';
import type { AsyncTask } from '@/stores/tasksStore.js';

interface TaskCardProps {
    task: AsyncTask;
    onAccept?: (taskId: string) => void;
    onReject?: (taskId: string) => void;
    onDelete?: (taskId: string) => void;
    onViewDetails?: (taskId: string) => void;
}

export function TaskCard({ task, onAccept, onReject, onDelete, onViewDetails }: TaskCardProps) {
    const isIncoming = task.direction === 'incoming';
    const isPendingApproval = task.status === 'pending_approval';
    const isWorking = task.status === 'working';
    const hasCompleted = task.status === 'completed';

    return (
        <div
            className={cn(
                'p-3 rounded-lg border transition-all',
                'hover:shadow-sm hover:border-primary/30',
                isPendingApproval && 'border-orange-400/50 bg-orange-50 dark:bg-orange-950/20',
                hasCompleted && 'opacity-60 hover:opacity-100',
                hasCompleted && 'cursor-pointer'
            )}
            onClick={() => hasCompleted && onViewDetails?.(task.taskId)}
        >
            {/* Header: Direction badge + Agent name + Status */}
            <div className="flex items-start gap-2 mb-2">
                <Badge
                    variant="outline"
                    className={cn(
                        'text-xs px-1.5 py-0 h-5 flex items-center gap-1',
                        isIncoming ? 'text-green-600 border-green-400/50' : 'text-blue-600 border-blue-400/50'
                    )}
                >
                    {isIncoming ? <ArrowLeft className="h-3 w-3" /> : <ArrowRight className="h-3 w-3" />}
                    {isIncoming ? 'In' : 'Out'}
                </Badge>

                <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">
                        {task.remoteAgentName || new URL(task.remoteAgent).host}
                    </div>
                </div>

                {isWorking && <Loader2 className="h-4 w-4 text-yellow-600 animate-spin flex-shrink-0" />}
            </div>

            {/* Message */}
            <p className="text-sm text-foreground/80 line-clamp-2 mb-2 pl-0.5">
                {task.request.text}
            </p>

            {/* Actions for pending approval */}
            {isIncoming && isPendingApproval && (
                <div className="flex gap-2 mt-3 pt-3 border-t">
                    <Button
                        size="sm"
                        className="flex-1 h-8 bg-green-600 hover:bg-green-700"
                        onClick={(e) => {
                            e.stopPropagation();
                            onAccept?.(task.taskId);
                        }}
                    >
                        <Check className="h-3.5 w-3.5 mr-1" />
                        Accept
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 h-8 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/50"
                        onClick={(e) => {
                            e.stopPropagation();
                            onReject?.(task.taskId);
                        }}
                    >
                        <X className="h-3.5 w-3.5 mr-1" />
                        Reject
                    </Button>
                </div>
            )}

            {/* Delete option for completed/failed tasks */}
            {!isPendingApproval && (
                <div className="flex justify-end mt-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete?.(task.taskId);
                        }}
                    >
                        Delete
                    </Button>
                </div>
            )}

            {/* Timestamp */}
            <div className="text-xs text-muted-foreground/60 mt-2 pl-0.5">
                {new Date(task.createdAt).toLocaleString()}
            </div>
        </div>
    );
}
